import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import PropTypes from 'prop-types'
import { Flex, Box } from 'reflexbox/styled-components'
import { graphql } from 'gatsby'
import compact from 'lodash/compact'
import kebabCase from 'lodash/kebabCase'

import { H2, H3, H4 } from 'lib/ui/primitives'
import media from 'media'
import theme from 'theme'

import Layout from 'components/page/layouts/GatsbyLayout'
import Section from 'components/page/Section'
import SectionHeader from 'components/page/Section/Header'
import SubSection, {
  SubSectionContainer,
} from 'components/page/Section/SubSection'
import Helmet from 'components/page/Helmet'
import Hero, { Subtitle, Title, Eyebrow, CTAContainer } from 'components/Hero'
import UniversalLink from 'components/UniversalLink'
import Button from 'components/Button'
import { HeroButtonContainer } from 'components/ButtonContainer'
import InlineTestimonial, {
  Quote,
  StyledQuote,
  PADDING_X,
  HeadshotContainer,
} from 'components/Testimonial/InlineTestimonial'
import SplitContainer, { Row } from 'components/NewSplitContainer'
import FormIconForm from 'components/illustrations/forms/FormIconForm'
import FormIconMagnifyingGlass from 'components/illustrations/forms/FormIconMagnifyingGlass'
import SimpleValueProps from 'components/SimpleValueProps'
import * as logos from 'components/logo/logos'
import ComplianceLogoBar from 'components/logo/ComplianceLogoBar'
import { LogoRow } from 'components/logo/LogoBar'
import { LogoContainer } from 'components/logo'
import FAQs from 'components/FAQs'
import ContactAnvil from 'components/Signup/ContactAnvil'
import VideoModal from 'components/VideoModal'

import MarketingIconReliability from 'components/illustrations/marketing-icons/MarketingIconReliability'
import MarketingIconEnterprise from 'components/illustrations/marketing-icons/MarketingIconEnterprise'
import MarketingIconCompliance from 'components/illustrations/marketing-icons/MarketingIconCompliance'

import BlogPosts from './components/BlogPosts'
import TrustedByLogoBar from './components/TrustedByLogoBar'

import useSanityTestimonial from 'hooks/sanity/useSanityTestimonial'
// import useRefCallback from 'hooks/useRefCallback'

import { getSocialCoverURL, APP_URL, SCROLL_NAME_CONTACT_SALES } from 'helpers'
import renderNavbar from 'helpers/renderNavbar'
import { GREEN_UNDERLINE, FORMS_SLUG } from 'consts'

import IconCheck from '@anvilco-pkg/ui/components/Icon/IconCheck'
import IconExclamation from '@anvilco-pkg/ui/components/Icon/IconExclamation'
import CircleIcon from '@anvilco-pkg/ui/components/CircleIcon'
import Markdown from '@anvilco-pkg/ui/components/Markdown'

// ALL THE HERO ASSETS
import InsuranceHeroAsset from './page-assets/insurance/HeroAsset'
import HRHeroAsset from './page-assets/human-resources/HeroAsset'
import FinanceHeroAsset from './page-assets/finance/HeroAsset'
import HealthcareHeroAsset from './page-assets/healthcare/HeroAsset'
import RealEstateHeroAsset from './page-assets/real-estate/HeroAsset'
import DeveloperApproach from './page-assets/shared/DeveloperApproach'

import CollectAsset from './assets/Collect'
import SignAsset from './assets/Sign'
import GenerateAsset from './assets/Generate'
import CodeAsset from './assets/Code'

import onbrandExperienceWebp from './assets/onbrand-experience.webp'
import sesoPlaceholder from 'components/videoPlaceholders/sesoPlaceholder.webp'
import vouchPlaceholder from 'components/videoPlaceholders/vouchPlaceholder.webp'
import PlayButton from 'components/PlayButton'
import PDFTemplatesTestDrive from 'components/test-drive/PDFTemplatesTestDrive'

// TODO: CANDYCODE migrate to next.js

const FORM_SCROLL_NAME = 'contact-sales'

const heroes = {
  insurance: InsuranceHeroAsset,
  'human-resources': HRHeroAsset,
  finance: FinanceHeroAsset,
  healthcare: HealthcareHeroAsset,
  'real-estate': RealEstateHeroAsset,
}

const videoPlaceholders = {
  seso: sesoPlaceholder,
  vouch: vouchPlaceholder,
}

const StyledOnbrandExperience = styled.img.attrs({
  src: onbrandExperienceWebp,
})`
  margin: 0 auto;
  width: 100%;
  max-width: 614px;
  height: auto;
`

const FAQsContainer = styled(Flex).attrs({
  flexDirection: ['column', 'column', 'row'],
  alignItems: ['flex-start'],
  justifyContent: ['flex-start', 'flex-start', 'space-between'],
  mt: [8, 8, 20],
})``

const StyledFAQs = styled(FAQs)`
  ${media.desktop`
    width: 55%;
  `}

  ${media.giant`
    width: 63%;
  `}
`

const MoreQuestions = styled(Flex).attrs({
  flexDirection: ['column'],
  alignItems: ['flex-start'],
  mt: [30, 30, '12%', '7%'],
  width: [1, 1, 'fit-content'],
})`
  & > * {
    margin-top: 6px;
    margin-bottom: 6px;
  }
`

const Question = styled.div`
  ${media.desktop`
      font-weight: ${theme.fontWeights.normal};
  `}
`

const StyledCodeAsset = styled(CodeAsset)`
  display: none;
  width: 175px;
  height: fit-content;

  ${media.desktop`
    display: block;
  `}
`

const ValuePropsSection = styled(SubSection)`
  ${media.desktop`
    padding-bottom: 45px;
  `}
`

const StyledSimpleValueProps = styled(SimpleValueProps)`
  margin-top: 80px;
`

const StyledComplianceLogoBar = styled(ComplianceLogoBar)`
  padding-top: 0;
  padding-bottom: 0;
  margin-top: 16px;

  ${LogoRow} {
    justify-content: space-between;
  }

  ${LogoContainer} {
    margin-left: 0px;
    margin-right: 20px;
  }

  svg {
    width: 60px;
    height: 60px;
    opacity: 0.6;
  }

  @media (min-width: 900px) {
    svg {
      width: 64px;
      height: 64px;
    }

    ${LogoContainer} {
      margin-right: 28px;
    }
  }

  ${media.giant`
    ${LogoContainer} {
      margin-left: 0px;
      margin-right: 15px;
    }

    svg {
      width: 50px;
      height: 50px;
    }
  `}
`

const StyledSplitContainer = styled(SplitContainer)`
  ${H4} {
    font-weight: ${theme.fontWeights.bold};
  }

  p {
    margin-top: 12px;
  }

  .digital-experience {
    max-width: 500px;
    margin: 0 auto;
  }

  ${media.desktop`
    p {
      margin-top: 20px;
    }

    .digital-experience {
      max-width: 100%;
    }

    ${StyledOnbrandExperience} {
      position: relative;
      left: -75px;
      bottom: -2px;
    }
  `}

  ${Row} {
    flex-direction: column-reverse;

    ${media.desktop`
      flex-direction: row;
    `}
  }
`

const SplitText = styled(Box).attrs({
  mb: [40, 40, 40],
})``

const LinkedFormsSection = styled(SubSection)`
  display: flex;
  flex-direction: column;

  ${media.desktop`
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  `}
`

const StyledSubSection = styled(SubSection)`
  padding-top: 45px;
  padding-bottom: 0px;
`

const LinkedFormsHeader = styled(Box).attrs({
  width: [1, 1, 0.35, 0.4],
})`
  ${H4} {
    font-weight: ${theme.fontWeights.bold};
  }

  p {
    margin-top: 12px;
  }

  ${media.desktop`
    p {
      margin-top: 20px;
    }
  `}
`

const LinkedForms = styled(Flex).attrs({
  width: [1, 1, 0.55],
  flexDirection: ['column', 'row'],
  justifyContent: ['flex-start', 'space-between', 'flex-end'],
  alignItems: ['flex-start'],
  pt: [16, 16, 0],
})`
  position: relative;
`

const PhantomForm = styled(FormIconForm)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: 20px;

  ${media.tablet`
    display: none;
  `}
`

const HeroAsset = styled.svg.withConfig({
  shouldForwardProp: (prop) => ['pageId'].indexOf(prop) === -1,
})`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  display: none;

  ${media.desktop`
    right: ${({ pageId }) => (pageId === 'finance' ? '23px' : '30px')};
    display: block;
    width: 270px;
    height: fit-content;
  `}

  ${media.giant`
    right: ${({ pageId }) => (pageId === 'insurance' ? `60px` : pageId === 'finance' ? '23px' : `35px`)};
    width: unset;
  `}
`

const StyledHero = styled(Hero)`
  ${Subtitle} {
    font-size: 18px;
    max-width: 100%;

    ${media.desktop`
      max-width: 50%;
    `}

    ${media.giant`
      max-width: 40%;
    `}
  }

  h1 {
    font-weight: ${theme.fontWeights.bold};
  }

  ${Title} {
    max-width: 100%;

    ${media.desktop`
      max-width: 50%;
    `}
  }

  ${CTAContainer} {
    max-width: 100%;

    ${media.desktop`
      max-width: 90%;
    `}
  }

  ${Eyebrow} {
    color: ${theme.colors.blues[50]};
    font-weight: ${theme.fontWeights.normal};

    ${media.desktop`
      margin-bottom: 50px;
      font-size: 21px;
    `}
  }
`

const AnvilForBlockContainer = styled(Box).attrs({
  width: [1, 1, 0.5],
  maxWidth: [null, null, 400],
  mb: [30, 30, 0],
  ml: [0, 0, 50],
})`
  position: relative;
  right: -15px;
`

const AnvilForBlockTitle = styled(Box).attrs({
  width: [1],
  fontSize: 24,
  mb: [12, 12, 20],
})`
  position: relative;
  font-weight: ${theme.fontWeights.bold};
`

const AnvilForBlockIcon = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: -50px;
`

const AnvilForBlockContent = styled.p`
  position: relative;
  z-index: 10;
`

const AnvilForHeader = styled(Flex).attrs({
  flexDirection: ['column', 'column', 'row'],
  justifyContent: 'space-evenly',
  pl: [50, 50, 0],
})`
  gap: 30px;
`

const CaseStudySubSection = styled(SubSection).attrs({})`
  background: ${theme.colors.blues[10]};
  display: flex;
  flex-direction: column-reverse;
  width: 100%;

  ${media.desktop`
    flex-direction: row;
    width: 58%;
  `}
`

const CaseStudyAssetSubSection = styled(SubSection)
  .attrs({
    padding: '40px 40px 75px',
  })
  .withConfig({
    shouldForwardProp: (prop) =>
      ['allowFullWidthAsset', 'stickToBottom'].indexOf(prop) === -1,
  })`
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: ${theme.colors.blues[20]};
  width: 100%;
  text-align: center;

  ${({ stickToBottom }) =>
    stickToBottom &&
    `
    padding-bottom: 0px;
  `}

  ${({ allowFullWidthAsset }) =>
    !allowFullWidthAsset
      ? `
      * {
        max-width: 490px;
      }
    `
      : media.desktop`
        max-width: 540px;
    `}

  p {
    margin: 10px auto 30px !important;
  }

  ${media.desktop`
    width: 42%;
    flex-direction: column;
  `}

  ${media.giant`
    width: fit-content;
  `}
`

const Spacer = styled(Box).attrs({ flex: 1 })``

const CaseStudyAssetText = styled(Box).attrs({
  mt: [30, 30, 0],
})`
  font-size: 24px;
  line-height: 31px;
  font-weight: ${theme.fontWeights.bold};

  .case-study-logo {
    width: fit-content;
    height: 67px;
  }
`

const CaseStudy = styled(Box).attrs({
  width: [1],
})``

const CaseStudyAsset = styled.svg.withConfig({
  shouldForwardProp: (prop) => ['allowFullWidthAsset'].indexOf(prop) === -1,
})`
  width: 100%;
  height: 100%;

  ${media.desktop`
    display: none;
  `}

  ${media.giant`
    display: block;
    height: auto;
    
    ${({ allowFullWidthAsset }) =>
      allowFullWidthAsset &&
      `
      position: absolute;
      bottom: 0;
      height: fit-content;
    `}
  `}
`

const CaseStudyDesktopAsset = styled.svg`
  display: none;
  width: 100%;
  height: 100%;

  ${media.desktop`
    display: block;
  `}

  ${media.giant`
    display: none;
  `}
`

const CaseStudyTitle = styled(H3)`
  font-weight: ${theme.fontWeights.bold};
  margin-bottom: 24px;
`

const CaseStudyButton = styled(Button)`
  margin-top: 40px;
  width: 100%;

  ${media.desktop`
    margin-left: 0;
    width: fit-content;
  `}

  ${media.giant`
    margin-left: ${PADDING_X}px;
  `}
`

const StyledInlineTestimonial = styled(InlineTestimonial)`
  ${HeadshotContainer} {
    border-radius: 50%;
    overflow: hidden;
  }
  ${media.desktop`
    ${Quote} {
      font-size: 16px;
    }

    ${StyledQuote} {
      display: none;
    }

    padding-left: 0;
    
  `}

  ${media.giant`
    ${Quote} {
      font-size: 24px;
      line-height: 36px;
    }

    padding-left: ${PADDING_X}px;

    ${StyledQuote} {
      display: block;
    }
  `}
`

const StyledSubSectionContainer = styled(SubSectionContainer).attrs({
  flexDirection: ['column-reverse', 'column-reverse', 'row-reverse'],
})`
  position: relative;
  z-index: 10;
`

const AnvilForBlock = ({ title, children, icon }) => (
  <AnvilForBlockContainer>
    <AnvilForBlockTitle>
      <AnvilForBlockIcon>{icon}</AnvilForBlockIcon>
      {title}
    </AnvilForBlockTitle>
    <AnvilForBlockContent>{children}</AnvilForBlockContent>
  </AnvilForBlockContainer>
)

AnvilForBlock.propTypes = {
  title: PropTypes.any,
  children: PropTypes.any,
  icon: PropTypes.any,
}

const CardContainer = styled(Flex).attrs({
  flexDirection: ['column', 'column', 'row'],
})`
  ${media.desktop`
    gap: 50px;
  `}
`

const Card = styled(Flex).attrs({
  width: [1],
  flexDirection: ['row', 'row', 'column'],
  alignItems: ['center', 'center', 'unset'],
  mb: [30, 0],
})``

const CardText = styled(Box).attrs({
  ml: [30, 30, 0],
  maxWidth: [null, null, 300],
})``

const CardTitle = styled(Box)
  .attrs({
    mb: [10],
    mx: ['auto', 'unset'],
    fontSize: [18, 18, 20, theme.fontSizes[120]],
  })
  .withConfig({
    shouldForwardProp: (prop) => ['isMobile'].indexOf(prop) === -1,
  })`
  font-weight: ${theme.fontWeights.bold};

  display: ${({ isMobile }) => (isMobile ? 'block' : 'none')};

  ${media.desktop`
    display: ${({ isMobile }) => (isMobile ? 'none' : 'block')};
  `}
`

const CardIcon = styled.svg`
  max-width: 120px;
  max-height: 120px;
  min-width: 120px;
  min-height: 120px;
  width: 100%;
  height: fit-content;
  margin: 0 auto 16px;

  ${media.tablet`
     max-width: 196px;
    max-height: 196px;
    min-width: 196px;
    min-height: 196px;
  `}

  ${media.desktop`
     max-width: unset;
      max-height: unset;
      min-width: unset;
      min-height: unset;
  `}
`

const Description = styled(Box).attrs({
  as: 'p',
})``

const PlatformValueProps = ({ items }) => {
  const renderCardTitle = (title, link, isMobile) => {
    return (
      <UniversalLink to={link}>
        <CardTitle isMobile={isMobile}>{title}</CardTitle>
      </UniversalLink>
    )
  }
  return (
    <CardContainer>
      {items.map(({ icon: Icon, title, link, description }, index) => (
        <Card key={title}>
          {renderCardTitle(title, link, false)}
          <CardIcon as={Icon} />
          <CardText>
            {renderCardTitle(title, link, true)}
            <Description>{description}</Description>
          </CardText>
        </Card>
      ))}
    </CardContainer>
  )
}

PlatformValueProps.propTypes = {
  items: PropTypes.any,
}

const FormContainer = styled(Flex).attrs({
  flexDirection: ['column'],
  alignItems: ['flex-start', 'flex-start', 'center'],
  justifyContent: 'flex-end',
  my: [6, 6, 0],
})`
  ${media.desktop`
    width: 25%;
  `}

  ${media.giant`
    min-width: 130px;
  `}

  @media (min-width: 1150px) {
    min-width: 142px;
  }

  ${media.beyondGiant`
    min-width: 150px;
  `}
`

const FormIcon = styled.svg`
  margin-bottom: 20px;
  width: 100%;
  display: none;

  ${media.tablet`
    display: block;
  `}
`

const FormLink = styled(UniversalLink).withConfig({
  shouldForwardProp: (prop) => ['offsetLeft'].indexOf(prop) === -1,
})`
  text-align: left;
  max-width: 100%;

  ${media.desktop`
    max-width: 130px;
    text-align: center;
  `}

  ${({ offsetLeft }) =>
    offsetLeft &&
    media.desktop`
    position: relative;
    left: -12px;
  `}
`

const FormItem = ({ title, to, icon, offsetLeft, ...others }) => (
  <FormContainer {...others}>
    <FormIcon as={icon} />
    <FormLink offsetLeft={offsetLeft} to={to}>
      {title}
    </FormLink>
  </FormContainer>
)

FormItem.propTypes = {
  title: PropTypes.any,
  to: PropTypes.any,
  icon: PropTypes.any,
  offsetLeft: PropTypes.any,
}

const HeroTitleWrapper = styled(Box).attrs({
  as: 'span',
})`
  display: block;
`

const StyledSectionHeaderCentered = styled(SectionHeader)`
  justify-content: center;

  ${H2} {
    width: 100%;
    text-align: center;
  }
`

const HighlightedSpan = styled(Box)
  .withConfig({
    shouldForwardProp: (prop) => ['bgColor'].indexOf(prop) === -1,
  })
  .attrs({
    as: 'span',
    padding: '0.125em 0',
  })`
  background-color: ${({ bgColor }) => bgColor};
`

const splitBySubstrings = (originalString, substrings) => {
  const result = []
  let lastIndex = 0

  substrings.forEach((substring) => {
    const index = originalString.indexOf(substring)
    if (index !== -1) {
      // Push text before the substring
      result.push({
        text: originalString.slice(lastIndex, index),
        underlined: false,
      })
      // Push the substring
      result.push({ text: substring, underlined: true })
      // Update the lastIndex
      lastIndex = index + substring.length
    }
  })

  // Push the remaining text after the last substring
  if (lastIndex < originalString.length) {
    result.push({ text: originalString.slice(lastIndex), underlined: false })
  }

  return result
}

const StyledMarkdown = styled(Markdown)`
  display: inline;

  * {
    display: inline;
  }

  a {
    font-weight: inherit !important;
  }
`

const defaultFaqs = [
  {
    question: 'Is Anvil customizable?',
    answer: `Yes. Anvil can dynamically determine which questions are asked in a webform and which PDFs get filled, style the entire form filling experience with CSS, determine the order in which e-signatures are elicited, and even format the output of the final documents. If you find something not customizable, [let us know](mailto:support@useanvil.com).`,
  },
  {
    question: 'How does Anvil help my team build and scale faster?',
    answer:
      'Paperwork is table-stakes for many businesses but carries an opportunity cost for developers working on core, differentiated features. Our SDK jumpstarts the software development process for documents so you can launch new products faster, saving your engineers months of document-related development time.',
  },
  {
    question: 'How much is Anvil relative to building it all in-house?',
    answer:
      'It depends on a number of variables (such as the number of developers resourced for building in-house, their salaries, etc.), but we’ve observed customers reducing costs, including time saved, on the order of 3x up to 10x.',
  },
]

const CaseStudyVideoAssetContainer = styled(Box)`
  position: relative;
  height: 370px;
  cursor: pointer;
  width: calc(100% + 40px);

  ${media.desktop`
    width: calc(100% + 100px);
  `}

  ${media.giant`
    width: calc(100% + 80px);
  `}
`

const StyledVideoAssetPlaceholderImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const CaseStudyVideoAssetInner = styled(Box)`
  position: relative;
  width: 100%;
  height: 100%;
`

const CaseStudyVideoPersonInfo = styled(Box)`
  text-align: left;
  font-size: 22px;
  line-height: 1.3;
  font-weight: 700;
  position: absolute;
  top: 25px;
  left: 30px;
`

const CaseStudyVideoPersonTitle = styled(Box)`
  font-weight: 500;
  font-size: 17px;
  line-height: 1.5;
`

const StyledTitle = styled(H3)``

const StyledSectionHeader = styled(SectionHeader)`
  ${StyledTitle} {
    width: 100%;
    text-align: center;
  }
`

const SolutionsTemplate = ({
  location: { pathname },
  data: {
    forms: { nodes: forms } = {},
    allSanityTestimonial,
    javascriptFrontmatter: {
      frontmatter: {
        name,
        seo,
        hero: { title, titleUnderlined, backgroundColor, titleMaxWidth },
        description,
        industry,
        logoBarLogos,
        challenge,
        solution,
        caseStudy,
        faqs,
        sectionBreak,
        platform,
        customerStories,
      },
    } = {},
    financeTitleImages,
    financeCustomerStories,
    financeSanityCustomerStories,
    insuranceTitleImages,
    insuranceCustomerStories,
    insuranceSanityCustomerStories,
    realEstateTitleImages,
    realEstateCustomerStories,
    realEstateSanityCustomerStories,
  },
  ...others
}) => {
  const [data, setData] = useState(null)
  const pageId = /^\/solutions\/(.+)\/$/.exec(pathname)[1]

  useEffect(() => {
    import(`./page-assets/${name}/lazy-loaded-components.js`).then((module) => {
      setData(module.default)
    })
  }, [])

  const titleSlices = splitBySubstrings(title, titleUnderlined)

  const actualMaxWidth = titleMaxWidth ?? '100%'

  const actualTitle = (
    <HeroTitleWrapper maxWidth={['100%', '100%', actualMaxWidth]}>
      {titleSlices.map(({ text, underlined }) =>
        underlined ? (
          <span className={GREEN_UNDERLINE} key={text}>
            {text}
          </span>
        ) : (
          text
        )
      )}
    </HeroTitleWrapper>
  )

  const renderHero = () => (
    <StyledHero
      verticalBreakpoint="tablet"
      title={actualTitle}
      subtitle={description}
      hideSpacer
      background={{
        color: backgroundColor,
        displayBreakpoint: 'desktop',
      }}
      renderAssets={() => <HeroAsset pageId={pageId} as={heroes[name]} />}
      renderCTA={() => (
        <HeroButtonContainer>
          <Button
            type="black"
            chevron={{ position: 'inner', direction: 'right' }}
            to={`${APP_URL}/signup`}
            size="large"
          >
            Sign up
          </Button>
          <Button
            type="outline-default"
            chevron={{ position: 'inner', direction: 'right' }}
            to={SCROLL_NAME_CONTACT_SALES}
            size="large"
          >
            Get a demo
          </Button>
        </HeroButtonContainer>
      )}
      vignette={{
        position: 'bottom-left',
        color: theme.colors.blacks[5],
        size: 'medium',
      }}
      eyebrow={`Anvil for ${industry}`}
    />
  )

  const renderLogoBar = () => (
    <Section type="logo-bar">
      <TrustedByLogoBar logos={logoBarLogos.map((logo) => logos[logo])} />
    </Section>
  )

  const renderAnvilFor = () => {
    const shouldRenderCustomerInsights = ![
      'human-resources',
      'healthcare',
    ].includes(pageId)
    const allowFullWidthAsset = pageId === 'human-resources' || caseStudy.video
    const stickToBottom =
      pageId === 'healthcare' || pageId === 'real-estate' || caseStudy.video
    const testimonial = useSanityTestimonial({ title: caseStudy?.quote })
    const blogSlug = caseStudy.blogSlug ?? caseStudy.logo?.toLowerCase()

    const challengeSlices = splitBySubstrings(
      challenge.text,
      challenge.highlight
    )

    const actualChallenge = (
      <>
        {challengeSlices.map(({ text, underlined }) =>
          underlined ? (
            <HighlightedSpan bgColor={theme.colors.reds[10]} key={text}>
              {text}
            </HighlightedSpan>
          ) : (
            <StyledMarkdown key={text}>{text}</StyledMarkdown>
          )
        )}
      </>
    )

    const solutionSlices = splitBySubstrings(solution.text, solution.highlight)

    const actualSolution = (
      <>
        {solutionSlices.map(({ text, underlined }) =>
          underlined ? (
            <HighlightedSpan bgColor={theme.colors.greens[20]} key={text}>
              <StyledMarkdown>{text}</StyledMarkdown>
            </HighlightedSpan>
          ) : (
            <StyledMarkdown key={text}>{text}</StyledMarkdown>
          )
        )}
      </>
    )

    return (
      <Section
        vignette={{
          position: 'top-right',
          color: theme.colors.blacks[5],
          size: 'small',
        }}
      >
        <AnvilForHeader>
          <AnvilForBlock
            icon={
              <CircleIcon
                background={theme.colors.red}
                color={theme.colors.black}
                size="medium"
              >
                <IconExclamation />
              </CircleIcon>
            }
            title="The challenge"
          >
            {actualChallenge}
          </AnvilForBlock>
          <AnvilForBlock
            icon={
              <CircleIcon
                background={theme.colors.green}
                color={theme.colors.black}
                size="medium"
              >
                <IconCheck />
              </CircleIcon>
            }
            title="The solution"
          >
            {actualSolution}
          </AnvilForBlock>
        </AnvilForHeader>
        <StyledSubSectionContainer>
          <CaseStudySubSection>
            <CaseStudy>
              <CaseStudyTitle>{caseStudy?.title}</CaseStudyTitle>
              <StyledInlineTestimonial
                quote={testimonial.quoteElement}
                author={testimonial.author}
                title={`${testimonial.jobTitle}, ${testimonial.organization}`}
                authorHeadshot={testimonial.authorHeadshot}
                isVertical
                withQuoteMarks
                quoteColor={theme.colors.blue}
              />
              <CaseStudyButton
                to={`/blog/customer-stories/${blogSlug}/`}
                type="black"
                chevron={{ position: 'inner', direction: 'right' }}
                size="large"
              >
                See the full case study
              </CaseStudyButton>
            </CaseStudy>
          </CaseStudySubSection>
          <CaseStudyAssetSubSection
            allowFullWidthAsset={allowFullWidthAsset}
            stickToBottom={stickToBottom}
          >
            <CaseStudyAssetText>
              {logos[caseStudy?.logo]?.getImage({
                className: 'case-study-logo',
              })}
              <p>{caseStudy?.description}</p>
            </CaseStudyAssetText>
            {stickToBottom && <Spacer />}
            {caseStudy.video ? (
              <VideoModal
                title={caseStudy.title}
                description={caseStudy.description}
                videoId={caseStudy.video.youtubeId}
                renderPlaceholder={({ onClick }) => {
                  return (
                    <CaseStudyVideoAssetContainer onClick={onClick}>
                      <CaseStudyVideoAssetInner>
                        <CaseStudyVideoPersonInfo>
                          {caseStudy.video.name}
                          <CaseStudyVideoPersonTitle>
                            {caseStudy.video.title}
                          </CaseStudyVideoPersonTitle>
                        </CaseStudyVideoPersonInfo>
                        <PlayButton />
                        <StyledVideoAssetPlaceholderImg
                          src={videoPlaceholders[caseStudy.logo]}
                          alt={caseStudy.logo}
                        />
                      </CaseStudyVideoAssetInner>
                    </CaseStudyVideoAssetContainer>
                  )
                }}
              />
            ) : (
              <>
                <CaseStudyAsset
                  as={data?.asset}
                  allowFullWidthAsset={allowFullWidthAsset}
                />
                <CaseStudyDesktopAsset as={data?.desktopAsset} />
              </>
            )}
          </CaseStudyAssetSubSection>
        </StyledSubSectionContainer>
        {shouldRenderCustomerInsights && (
          <>
            <SubSection>
              <StyledSectionHeader
                title={customerStories?.label ?? 'Customer insights'}
                styleTitleComponent={StyledTitle}
              />
            </SubSection>
            <BlogPosts
              pageId={pageId}
              finance={{
                titleImages: financeTitleImages.nodes,
                customerStories: financeCustomerStories.nodes,
                sanityCustomerStories: financeSanityCustomerStories.nodes,
              }}
              insurance={{
                titleImages: insuranceTitleImages.nodes,
                customerStories: insuranceCustomerStories.nodes,
                sanityCustomerStories: insuranceSanityCustomerStories.nodes,
              }}
              realEstate={{
                titleImages: realEstateTitleImages.nodes,
                customerStories: realEstateCustomerStories.nodes,
                sanityCustomerStories: realEstateSanityCustomerStories.nodes,
              }}
            />
          </>
        )}
      </Section>
    )
  }

  const renderPlatform = () => {
    const items = [
      {
        icon: CollectAsset,
        title: 'Collect data with Webforms',
        description: `Use Anvil's AI Webform generation to build on-brand mobile-friendly Webforms.`,
        link: '/products/webforms/',
      },
      {
        icon: GenerateAsset,
        title: 'Fill or generate PDFs',
        description:
          'One API call is all it takes to fill PDFs over API or dynamically generate new ones. ',
        link: '/products/pdf-services/',
      },
      {
        icon: SignAsset,
        title: 'Get it e-signed',
        description: `Identify and assign signers with Anvil's Document AI and embed white-labeled e-signatures into your app.`,
        link: '/products/etch/',
      },
    ]

    const platformTextCol = {
      width: [1, 1, 0.4],
      renderChildren: () => (
        <SplitText>
          <H4>
            {platform.computerTitle ??
              'Provide the best digital experience for your clients'}
          </H4>
          <p>{platform.computerText}</p>
          <UniversalLink to="/docs/api/white-labeling/">
            See white labeling docs
          </UniversalLink>
        </SplitText>
      ),
    }

    const platformAssetCol = {
      width: [1, 1, 0.55],
      renderChildren: () => <StyledOnbrandExperience />,
    }

    const rows = [[platformAssetCol, platformTextCol]]

    const platformTitleUnderlined = (
      <span className={GREEN_UNDERLINE}>{platform.titleUnderlined}</span>
    )

    const actualPlatformTitle = platform.titleUnderlined ? (
      <>
        {platform.title.substring(
          0,
          platform.title.indexOf(platform.titleUnderlined)
        )}
        {platformTitleUnderlined}
        {platform.title.substring(
          platform.title.indexOf(platform.titleUnderlined) +
            platform.titleUnderlined.length
        )}
      </>
    ) : (
      platform.title
    )

    const shouldRenderLinkedForms = pageId !== 'healthcare'
    const industryQuery = platform.formsQuery ?? industry
    const seeMoreLink =
      pageId === 'finance'
        ? `/${FORMS_SLUG}/`
        : `/${FORMS_SLUG}/?q=${industryQuery.replace('-', ' ')}`

    return (
      <Section>
        <StyledSectionHeaderCentered
          title={<>{actualPlatformTitle}</>}
          titleFullWidth
        />
        <PlatformValueProps items={items} />
        <StyledSubSection pageId={pageId} background={theme.colors.blues[10]}>
          <StyledSplitContainer rows={rows} />
        </StyledSubSection>
        {shouldRenderLinkedForms && (
          <LinkedFormsSection background={theme.colors.greens[20]}>
            <LinkedFormsHeader>
              <H4>Fast-track development with prebuilt PDF templates</H4>
              <p>
                Get started with a prebuilt forms that you can customize and
                begin collecting responses to in minutes.
              </p>
            </LinkedFormsHeader>
            {/* TODO: query gatsby for forms. if someone wants to override, let them? nah */}
            <LinkedForms>
              {compact(
                forms
                  .filter(({ frontmatter: { title, slug } }) => {
                    return pageId !== 'finance' || !slug.includes('irs')
                  })
                  .map(
                    (
                      { frontmatter: { title, slug, tags, shortTitle } },
                      index
                    ) =>
                      index < 3 ? (
                        <FormItem
                          key={title}
                          to={`/${FORMS_SLUG}/${kebabCase(tags[0].toLowerCase())}/${slug}/`}
                          title={shortTitle ?? title}
                          icon={FormIconForm}
                          offsetLeft
                        />
                      ) : null
                  )
              )}
              <FormItem
                to={seeMoreLink}
                title="See more forms"
                icon={FormIconMagnifyingGlass}
                style={{ minWidth: '116px', marginTop: 19 }}
              />
              <PhantomForm />
            </LinkedForms>
          </LinkedFormsSection>
        )}
      </Section>
    )
  }

  const renderSecurity = () => {
    const items = [
      {
        icon: <MarketingIconEnterprise />,
        title: 'Bank-level encryption',
        description:
          'We apply industry standard encryption to all data in transit. Sensitive data gets an extra layer of encryption.',
      },
      {
        icon: <MarketingIconReliability />,
        title: 'Trusted reliability',
        description:
          'We go beyond providing best in class uptime and deliver super-fast performance at any scale.',
      },
      {
        icon: <MarketingIconCompliance />,
        title: 'Fully compliant',
        description:
          "Products built on our compliant platform are compliant too - that's nice.",
        renderCTA: () => <StyledComplianceLogoBar hideTitle />,
      },
    ]
    return (
      <Section>
        <ValuePropsSection>
          <H2 style={{ fontWeight: theme.fontWeights.bold }}>
            Anvil is secure & compliant
          </H2>
          <StyledSimpleValueProps items={items} />
        </ValuePropsSection>
      </Section>
    )
  }

  const renderFAQ = () => {
    return (
      <Section
        vignette={{
          size: 'small',
          position: 'top-right',
          color: theme.colors.blacks[5],
        }}
      >
        <SubSection background={theme.colors.blacks[5]}>
          <H3 style={{ fontWeight: theme.fontWeights.bold }}>FAQs</H3>
          <FAQsContainer>
            <StyledFAQs items={[...defaultFaqs, ...faqs]} />
            <MoreQuestions>
              <StyledCodeAsset />
              <Question>Have more questions?</Question>
              <UniversalLink to="/help/">Explore the Help Center</UniversalLink>
              <UniversalLink to="/docs/">
                See our developer documentation
              </UniversalLink>
            </MoreQuestions>
          </FAQsContainer>
        </SubSection>
      </Section>
    )
  }

  const renderSignup = () => {
    return <ContactAnvil scrollName={FORM_SCROLL_NAME} />
  }

  return (
    <Layout themeId="solutions">
      <Helmet
        title={seo.title}
        meta={{
          description: seo.description,
          keywords: seo.keywords,
        }}
        pathname={pathname}
        socialImageURL={getSocialCoverURL('industry', name)}
      />
      {renderNavbar()}
      {renderHero()}
      {renderLogoBar()}
      {renderAnvilFor()}
      {renderPlatform()}
      {renderFAQ()}
      <PDFTemplatesTestDrive />
      {renderSignup()}
      <Section>
        <DeveloperApproach />
      </Section>
      {renderSecurity()}
    </Layout>
  )
}

SolutionsTemplate.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }),
  data: PropTypes.any,
}

export const pageQuery = graphql`
  query SolutionsTemplateQuery($id: String, $industries: [String]) {
    forms: allMarkdownRemark(
      sort: { frontmatter: { title: DESC } }
      filter: {
        frontmatter: {
          tags: { in: $industries }
          type: { in: ["Form"] }
          onIndustryPage: { eq: true }
        }
      }
    ) {
      nodes {
        frontmatter {
          slug
          title
          tags
          summary
          type
          shortTitle
        }
      }
    }
    javascriptFrontmatter(id: { eq: $id }) {
      frontmatter {
        name
        seo {
          title
          description
          keywords
        }
        hero {
          title
          titleUnderlined
          backgroundColor
          titleMaxWidth
        }
        description
        industry
        challenge {
          text
          highlight
        }
        solution {
          text
          highlight
        }
        caseStudy {
          description
          logo
          quote
          title
          blogSlug
          video {
            youtubeId
            title
            name
          }
        }
        logoBarLogos
        faqs {
          question
          answer
        }
        sectionBreak
        platform {
          computerTitle
          computerText
          headerText
          title
          titleUnderlined
          formsQuery
        }
        customerStories {
          label
          description
        }
      }
    }
    allSanityTestimonial {
      nodes {
        _rawQuote(resolveReferences: { maxDepth: 10 })
        title
        quoteAuthor {
          image {
            asset {
              gatsbyImageData
            }
          }
          jobTitle
          lastName
          organization {
            organizationName
          }
          firstName
        }
      }
    }
    financeTitleImages: allSanityImageAsset(
      filter: {
        title: {
          in: [
            "How Sunrise Banks used Anvil to multiply their loan processing capacity by 5X"
            "How Mainstreet automated their tax credit process using Anvil's PDF filling API"
            "Case Study: First Ascent Asset Management, LLC"
            "Case Study: Catalyst Private Wealth"
            "Case Study: Octave Wealth"
          ]
        }
      }
    ) {
      nodes {
        gatsbyImageData
        url
        title
      }
    }
    financeCustomerStories: allMarkdownRemark(
      filter: {
        frontmatter: {
          slug: {
            in: [
              "first-ascent"
              "catalyst-private-wealth"
              "octave-wealth"
              "sunrise-banks"
              "mainstreet"
            ]
          }
        }
      }
    ) {
      nodes {
        frontmatter {
          title
          summary
          slug
          tags
        }
        internal {
          type
        }
      }
    }
    financeSanityCustomerStories: allSanityBlogPost(
      filter: { article: { slug: { in: ["gridline", "carta"] } } }
    ) {
      nodes {
        _rawArticle(resolveReferences: { maxDepth: 10 })
        article {
          title
          slug
          tags
        }
        heroImage {
          asset {
            gatsbyImageData
            publicUrl
          }
        }
        internal {
          type
        }
      }
    }
    insuranceTitleImages: allSanityImageAsset(
      filter: {
        title: {
          in: [
            "How Vouch digitized their insurance customer onboarding flow"
            "Transforming Insurance Paperwork into Programmable Solutions"
            "Document Automation For Insurance: 3 Strategies For More Efficient Workflows"
            "Modernizing Standard Insurance Forms: Embracing Next-Gen Parameters"
          ]
        }
      }
    ) {
      nodes {
        gatsbyImageData
        url
        title
      }
    }
    insuranceCustomerStories: allMarkdownRemark(
      filter: {
        frontmatter: {
          slug: {
            in: [
              "vouch"
              "making-insurance-docs-programmable"
              "insurance-document-automation"
              "acord-forms-2.0"
            ]
          }
        }
      }
    ) {
      nodes {
        frontmatter {
          title
          summary
          slug
          tags
        }
        internal {
          type
        }
      }
    }
    insuranceSanityCustomerStories: allSanityBlogPost(
      filter: { article: { slug: { in: ["ascend"] } } }
    ) {
      nodes {
        _rawArticle(resolveReferences: { maxDepth: 10 })
        article {
          title
          slug
          tags
        }
        heroImage {
          asset {
            gatsbyImageData
            publicUrl
          }
        }
        internal {
          type
        }
      }
    }
    realEstateTitleImages: allSanityImageAsset(
      filter: { title: { in: ["Case Study: Pacific Workplaces"] } }
    ) {
      nodes {
        gatsbyImageData
        url
        title
      }
    }
    realEstateCustomerStories: allMarkdownRemark(
      filter: { frontmatter: { slug: { in: ["pacific-workplaces"] } } }
    ) {
      nodes {
        frontmatter {
          title
          summary
          slug
          tags
        }
        internal {
          type
        }
      }
    }
    realEstateSanityCustomerStories: allSanityBlogPost(
      filter: { article: { slug: { in: ["inmofit", "here"] } } }
    ) {
      nodes {
        _rawArticle(resolveReferences: { maxDepth: 10 })
        article {
          title
          slug
          tags
        }
        heroImage {
          asset {
            gatsbyImageData
            publicUrl
          }
        }
        internal {
          type
        }
      }
    }
  }
`

export default SolutionsTemplate
